import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';

/**
 * Hook for managing announcements
 * @param {string} businessId - The current business ID
 * @param {boolean} fetchEmployees - Whether to also fetch employees for targeting
 */
export const useAnnouncements = (businessId, fetchEmployeesFlag = false) => {
  const [announcements, setAnnouncements] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnnouncements = useCallback(async () => {
    if (!businessId) {
      setAnnouncements([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/announcements?business_id=${businessId}`);
      setAnnouncements(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch announcements');
      console.error('Error fetching announcements:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  const fetchEmployeeList = useCallback(async () => {
    if (!businessId || !fetchEmployeesFlag) return;
    try {
      const res = await api.get(`/employees?business_id=${businessId}`);
      const list = Array.isArray(res.data) ? res.data : res.data.employees || [];
      setEmployees(list.filter(e => e.is_active !== false));
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  }, [businessId, fetchEmployeesFlag]);

  useEffect(() => {
    fetchAnnouncements();
    fetchEmployeeList();
  }, [fetchAnnouncements, fetchEmployeeList]);

  const createAnnouncement = async (announcementData) => {
    const res = await api.post('/announcements', { ...announcementData, business_id: businessId });
    setAnnouncements(prev => [res.data, ...prev]);
    return res.data;
  };

  const updateAnnouncement = async (announcementId, updates) => {
    const res = await api.put(`/announcements/${announcementId}`, updates);
    setAnnouncements(prev => prev.map(a => a.id === announcementId ? res.data : a));
    return res.data;
  };

  const deleteAnnouncement = async (announcementId) => {
    await api.delete(`/announcements/${announcementId}`);
    setAnnouncements(prev => prev.filter(a => a.id !== announcementId));
  };

  // Stats
  const stats = {
    total: announcements.length,
    urgent: announcements.filter(a => a.priority === 'urgent').length,
    important: announcements.filter(a => a.priority === 'important').length,
  };

  return {
    announcements,
    employees,
    loading,
    error,
    stats,
    fetchAnnouncements,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement
  };
};

/**
 * Hook for managing advances and loans
 */
export const useAdvancesLoans = (businessId) => {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAdvances = useCallback(async () => {
    if (!businessId) {
      setAdvances([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/advances?business_id=${businessId}`);
      setAdvances(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch advances');
      console.error('Error fetching advances:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchAdvances();
  }, [fetchAdvances]);

  const createAdvance = async (advanceData) => {
    const res = await api.post('/advances', { ...advanceData, business_id: businessId });
    setAdvances(prev => [res.data, ...prev]);
    return res.data;
  };

  const approveAdvance = async (advanceId, approvalData = {}) => {
    const res = await api.put(`/advances/${advanceId}/approve`, approvalData);
    setAdvances(prev => prev.map(a => a.id === advanceId ? res.data : a));
    return res.data;
  };

  const rejectAdvance = async (advanceId, reason = '') => {
    const res = await api.put(`/advances/${advanceId}/reject`, { reason });
    setAdvances(prev => prev.map(a => a.id === advanceId ? res.data : a));
    return res.data;
  };

  const recordRepayment = async (advanceId, amount, notes = '') => {
    const res = await api.post(`/advances/${advanceId}/repayment`, { amount, notes });
    setAdvances(prev => prev.map(a => a.id === advanceId ? res.data : a));
    return res.data;
  };

  const getStats = async () => {
    const res = await api.get(`/advances/stats?business_id=${businessId}`);
    return res.data;
  };

  return {
    advances,
    loading,
    error,
    fetchAdvances,
    createAdvance,
    approveAdvance,
    rejectAdvance,
    recordRepayment,
    getStats
  };
};

/**
 * Hook for managing appraisals
 */
export const useAppraisals = (businessId) => {
  const [appraisals, setAppraisals] = useState([]);
  const [dueAppraisals, setDueAppraisals] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAppraisals = useCallback(async () => {
    if (!businessId) {
      setAppraisals([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      const [appraisalsRes, dueRes, statsRes] = await Promise.all([
        api.get(`/appraisals?business_id=${businessId}`),
        api.get(`/appraisals/due?business_id=${businessId}`),
        api.get(`/appraisals/stats?business_id=${businessId}`)
      ]);
      setAppraisals(appraisalsRes.data);
      setDueAppraisals(dueRes.data);
      setStats(statsRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch appraisals');
      console.error('Error fetching appraisals:', err);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchAppraisals();
  }, [fetchAppraisals]);

  const createAppraisal = async (appraisalData) => {
    const res = await api.post('/appraisals', { ...appraisalData, business_id: businessId });
    await fetchAppraisals();
    return res.data;
  };

  const approveAppraisal = async (appraisalId, incrementData) => {
    const res = await api.put(`/appraisals/${appraisalId}/approve`, incrementData);
    await fetchAppraisals();
    return res.data;
  };

  const rejectAppraisal = async (appraisalId, reason = '') => {
    const res = await api.put(`/appraisals/${appraisalId}/reject`, { reason });
    await fetchAppraisals();
    return res.data;
  };

  const getHistory = async (employeeId) => {
    const res = await api.get(`/appraisals/history/${employeeId}?business_id=${businessId}`);
    return res.data;
  };

  const generateLetter = async (appraisalId) => {
    const res = await api.post(`/appraisals/${appraisalId}/letter`);
    return res.data;
  };

  return {
    appraisals,
    dueAppraisals,
    stats,
    loading,
    error,
    fetchAppraisals,
    createAppraisal,
    approveAppraisal,
    rejectAppraisal,
    getHistory,
    generateLetter
  };
};
